import {Component, HostListener, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {OAuthService} from 'angular-oauth2-oidc';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';
import {CookieService} from 'ngx-cookie-service';
import {ApplicationConstant} from './utilities/ApplicationConstant';
import { ConfigService, DataService, SessionService, SnackbarService } from './services';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
    isOpen = window.innerWidth >= 768;
    private hasUserToggledSidebar = false;
    title = 'SS&C Alerting';

    @HostListener('window:resize') onResize() {
        if (!this.hasUserToggledSidebar) {
            this.isOpen = window.innerWidth >= 768;
        }
    }

    onSidebarToggled(opened: boolean): void {
        this.isOpen = opened;
        this.hasUserToggledSidebar = true;
    }
    announcement: {
        dismissed?: boolean;
        dismissedHash?: number;
        current?: string;
        currentHash?: number;
        color?: string;
    } = {};

    constructor(private router: Router,
                private sessionService: SessionService,
                public dataService: DataService,
                public config: ConfigService,
                private snackbarService: SnackbarService,
                public oauthService: OAuthService,
                private cookieService: CookieService) {
    }

    ngOnInit() {
        // Init App with Admin mode on/off based on value in cookie!
        ApplicationConstant.toggleAdminRole(this.isAdminOn());

        // TODO: REPLACE getSettings() filter with this, wasn't working for some reason
        // this.dataService.getSetting('NEWS').subscribe((res: string) => this.newsText = res);
        this.dataService.getSettings().subscribe(res => {
            const news = res.find(setting => setting.name === 'NEWS');
            const newsColor = res.find(setting => setting.name === 'NEWS_COLOR');
            const announcementValue = news ? news.value : undefined;
            const dismissed = +sessionStorage.getItem('dismiss-announcement');
            this.announcement = {
                dismissed: !!dismissed,
                dismissedHash: dismissed,
                current: !!announcementValue && announcementValue,
                currentHash: !!announcementValue && this.hashCode(announcementValue),
                color: newsColor
            };

            // Change this manually because doing it in HTML will make it have a stroke
            this.announcement.color['value'] = "var(" + this.announcement.color['value'] + ")";
        });

        // Redirect back to same page on KeyCloak refresh
        const redirectURL = sessionStorage.getItem(this.config.get('sessionRedirectName'));
        if (redirectURL) {
            sessionStorage.removeItem(this.config.get('sessionRedirectName'));
            this.router.navigateByUrl(redirectURL);
        }

        let sBrowser;
        // browser support snackbar display.

        // TODO change this to a switch?
        if (navigator.userAgent.indexOf('Firefox') > -1) {
            sBrowser = 'Mozilla Firefox';
            // 'Mozilla/5.0 (X11; Ubuntu; Linux x86_64; rv:61.0) Gecko/20100101 Firefox/61.0'
        } else if (navigator.userAgent.indexOf('SamsungBrowser') > -1) {
            sBrowser = 'Samsung Internet';
            // 'Mozilla/5.0 (Linux; Android 9; SAMSUNG SM-G955F Build/PPR1.180610.011) AppleWebKit/537.36 (KHTML, like Gecko) SamsungBrowser/9.4 Chrome/67.0.3396.87 Mobile Safari/537.36
        } else if (navigator.userAgent.indexOf('Opera') > -1 || navigator.userAgent.indexOf('OPR') > -1) {
            sBrowser = 'Opera';
            // 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_14_0) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/70.0.3538.102 Safari/537.36 OPR/57.0.3098.106'
        } else if (navigator.userAgent.indexOf('Trident') > -1) {
            sBrowser = 'Microsoft Internet Explorer';
            // 'Mozilla/5.0 (Windows NT 10.0; WOW64; Trident/7.0; .NET4.0C; .NET4.0E; Zoom 3.6.0; wbx 1.0.0; rv:11.0) like Gecko'
        } else if (navigator.userAgent.indexOf('Edge') > -1) {
            sBrowser = 'Microsoft Edge';
            // 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.36 Edge/16.16299'
        } else if (navigator.userAgent.indexOf('Chrome') > -1) {
            sBrowser = 'Google Chrome or Chromium';
            // 'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Ubuntu Chromium/66.0.3359.181 Chrome/66.0.3359.181 Safari/537.36'
        } else if (navigator.userAgent.indexOf('Safari') > -1) {
            sBrowser = 'Apple Safari';
            // 'Mozilla/5.0 (iPhone; CPU iPhone OS 11_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/11.0 Mobile/15E148 Safari/604.1 980x1306'
        } else {
            sBrowser = 'unknown';
        }
        if (!navigator.userAgent.match('Chrome') || navigator.userAgent.match('Edge')) {
            this.snackbarService.openSnackBar(`Some functionality may be limited when using ${sBrowser}. For the best user experience, please use the latest version of Chrome.`, true);
        }

        this.sessionService.sessionExpired.subscribe(() => {
            this.oauthService.revokeTokenAndLogout();
            this.sessionService.logout();
        });
    }

    onToggleAdmin($event: MatSlideToggleChange) {
        this.cookieService.set('adminOn', '' + $event.checked);
        ApplicationConstant.toggleAdminRole($event.checked);
        window.location.reload();
    }

    shouldDisplayAnnouncement() {
        return !!this.announcement.current && this.announcement.dismissedHash !== this.announcement.currentHash;
    }

    dismissAnnouncement(msg: string) {
        this.announcement.dismissedHash = this.hashCode(msg);
        sessionStorage.setItem('dismiss-announcement', String(this.announcement.dismissedHash));
    }

    hashCode(str: string) {
        let hash = 0, i, chr;
        for (i = 0; i < str.length; i++) {
            chr = str.charCodeAt(i);
            // eslint-disable-next-line no-bitwise
            hash = ((hash << 5) - hash) + chr;
            // eslint-disable-next-line no-bitwise
            hash |= 0; // Convert to 32bit integer
        }
        return hash;
    }

    /**
     * Flag to maintain if user is a "true" admin no matter if in admin/non-admin view mode.
     */
    isTrueAdmin() {
        let isTrueAdmin = false;
        if (this.cookieService.check('isAdmin')) {
            isTrueAdmin = this.cookieService.get('isAdmin') === 'true';
        }
        return isTrueAdmin;
    }

    /**
     * Is the cookie flag set to admin/non-admin view mode?
     */
    isAdminOn() {
        let isAdminOn = this.isTrueAdmin();
        if (this.cookieService.check('adminOn')) {
            isAdminOn = this.cookieService.get('adminOn') === 'true';
        }
        return isAdminOn;
    }
}
